import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Send, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { useContactSubmit, HoneypotField, CaptchaField, SmsConsentField, CityZipFields, locationSchemaFields } from './contact/FormShared';
import { TUNE_UP_SERVICE, WINTER_SERVICE } from './TuneUpBanner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './ui/form';

const formSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().min(10, 'Please enter a valid phone number'),
  service: z.string().min(1, 'Please select a service'),
  message: z.string().min(10, 'Message must be at least 10 characters'),
  ...locationSchemaFields(z),
  consent: z.boolean().optional(),
  website: z.string().max(0).optional(),
});

const services = [
  'Residential Plumbing',
  'Commercial Plumbing',
  'Boilers',
  TUNE_UP_SERVICE,
  WINTER_SERVICE,
  'Water Heaters',
  'Remodeling',
  'Air Conditioning',
  'Other',
];

const inputClass = "border-slate-300 focus:border-cyan focus:ring-cyan";

const ContactForm = () => {
  const [searchParams] = useSearchParams();
  const presetService = services.includes(searchParams.get('service')) ? searchParams.get('service') : '';
  const formRef = useRef(null);

  useEffect(() => {
    if (presetService) formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [presetService]);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: { name: '', email: '', phone: '', city: '', zip: '', service: presetService, message: '', consent: false, website: '' },
  });

  const { onSubmit, isSubmitting, turnstileToken, setTurnstileToken } = useContactSubmit(form, {
    successMessage: 'Thank you! Your message has been sent. We will contact you soon.',
    formName: 'contact-page',
    buildPayload: ({ name, email, phone, service, message }) => ({ name, email, phone, service, message }),
  });

  return (
    <Form {...form}>
      <form ref={formRef} onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" data-testid="contact-form">
        <HoneypotField form={form} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-oswald uppercase text-navy">Name</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="Your Name" {...field} className={inputClass} data-testid="contact-name" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-oswald uppercase text-navy">Email</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="your@email.com" {...field} className={inputClass} data-testid="contact-email" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-oswald uppercase text-navy">Phone</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="(XXX) XXX-XXXX" {...field} className={inputClass} data-testid="contact-phone" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="service"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-oswald uppercase text-navy">Service Needed</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className={inputClass} data-testid="contact-service">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service} value={service}>{service}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <CityZipFields form={form} prefix="contact" inputClassName={inputClass} showLabels labelClassName="font-oswald uppercase text-navy text-sm font-medium" />
        </div>

        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-oswald uppercase text-navy">Message</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Tell us about your project or issue..."
                  rows={5}
                  {...field}
                  className={`${inputClass} resize-none`}
                  data-testid="contact-message"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <SmsConsentField form={form} testId="contact-consent" />
        <CaptchaField setToken={setTurnstileToken} />

        <Button
          type="submit"
          disabled={isSubmitting || !turnstileToken}
          className="btn-primary w-full md:w-auto"
          data-testid="contact-submit"
        >
          {isSubmitting ? (
            <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Sending...</>
          ) : (
            <><Send className="w-5 h-5 mr-2" />Send Message</>
          )}
        </Button>
      </form>
    </Form>
  );
};

export default ContactForm;
